import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  PlusCircle,
  User,
  MapPin,
  Phone,
  Mail,
  Home,
  FileText,
  Image,
  Pencil,
  Trash2,
  Search,
  Upload,
  Loader2,
  Eye,
  MoreHorizontal,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TablePagination from "@/components/masters/TablePagination";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/modal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api, { MEDIA_URL } from "@/services/api";

// Consumer statuses
const CONSUMER_STATUSES = ["active", "inactive", "pending", "suspended"];

interface Consumer {
  id: string;
  entryNumber: string;
  registrationNumber: string;
  firstName: string;
  lastName: string;
  fullName: string;
  categoryId: string;
  areaId: string;
  zoneId: string;
  divisionId: string;
  contactNumber: string;
  openingBalance: number;
  balanceType: string;
  status: string;
  alternativeNumber?: string;
  email?: string;
  photo?: string;
  idProof?: string;
  buildingNumber?: string;
  street?: string;
  city?: string;
  district?: string;
  state?: string;
  country?: string;
  category?: {
    id: string;
    name: string;
    code: string;
  };
  area?: {
    id: string;
    name: string;
  };
  zone?: {
    id: string;
    name: string;
    code: string;
  };
  division?: {
    id: string;
    name: string;
    code: string;
  };
}

interface MasterData {
  categories: Array<{ id: string; name: string; code: string }>;
  areas: Array<{ id: string; name: string }>;
  zones: Array<{ id: string; name: string; code: string }>;
  divisions: Array<{ id: string; name: string; code: string }>;
}

const ITEMS_PER_PAGE = 10;

// Helper function to capitalize first letter
const capitalizeFirstLetter = (str: string) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const ConsumersPage = () => {
  // Excel import confirmation popup state
  const [showExcelConfirm, setShowExcelConfirm] = useState(false);
  const [selectedExcelFile, setSelectedExcelFile] = useState<File | null>(null);
  const [consumers, setConsumers] = useState<Consumer[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedConsumerId, setSelectedConsumerId] = useState<string | null>(
    null
  );
  const [filters, setFilters] = useState({
    category: "all",
    area: "all",
    status: "all",
    query: "",
  });
  const [masterData, setMasterData] = useState<MasterData>({
    categories: [],
    areas: [],
    zones: [],
    divisions: [],
  });
  const [formData, setFormData] = useState({
    entryNumber: "",
    registrationNumber: "",
    firstName: "",
    lastName: "",
    categoryId: "",
    areaId: "",
    zoneId: "",
    divisionId: "",
    contactNumber: "",
    openingBalance: 0,
    balanceType: "receivable",
    status: "active",
    alternativeNumber: "",
    email: "",
    photo: "",
    idProof: "",
    buildingNumber: "",
    street: "",
    city: "",
    district: "",
    state: "",
    country: "",
  });
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const [viewConsumer, setViewConsumer] = useState<Consumer | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>("");

  // Fetch master data
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [categoriesRes, areasRes, zonesRes, divisionsRes] =
          await Promise.all([
            api.category.getCategories({ status: "true" }),
            api.area.getAreas(),
            api.zone.getZones({ status: "true" }),
            api.division.getDivisions({ status: "true" }),
          ]);

        setMasterData({
          categories: categoriesRes.docs || [],
          areas: areasRes.docs || [],
          zones: zonesRes.docs || [],
          divisions: divisionsRes.docs || [],
        });
      } catch (error) {
        console.error("Error fetching master data:", error);
        toast({
          title: "Error",
          description: error.message ?? "Failed to load master data",
          variant: "destructive",
        });
      }
    };

    fetchMasterData();
  }, []);

  // Fetch consumers
  const fetchConsumers = async () => {
    try {
      setIsLoading(true);
      const response = await api.consumer.getConsumers({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        query: filters.query.trim(),
        categoryId: filters.category !== "all" ? filters.category : undefined,
        areaId: filters.area !== "all" ? filters.area : undefined,
        status: filters.status !== "all" ? filters.status : undefined,
      });

      setConsumers(response.docs);
      setTotalPages(Math.ceil(response.totalDocs / ITEMS_PER_PAGE));
    } catch (error) {
      console.error("Error fetching consumers:", error);
      toast({
        title: "Error",
        description: error.message ?? "Failed to load consumers",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConsumers();
  }, [currentPage, filters]);

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleInputChange = (field: string, value: string | number) => {
    // Don't trim during typing, only store the value as is
    setFormData({ ...formData, [field]: value });
  };

  // Handler to allow only numeric input
  const handleNumericInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !/[0-9]/.test(e.key) &&
      e.key !== "Backspace" &&
      e.key !== "Tab" &&
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight"
    ) {
      e.preventDefault();
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      setIsLoading(true);
      const formDataObj = new FormData();
      formDataObj.append("files", file);
      const response = await api.authenticated.uploadFiles(formDataObj);
      // The Users.tsx expects response.data[0] as the image path
      const imageUrl = response.data ? response.data[0] : response.url;
      setFormData((prev) => ({ ...prev, photo: imageUrl }));
      setImagePreview(`${MEDIA_URL}${imageUrl}`);
      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      toast({
        title: "Error",
        description: error.message ?? "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (consumer: Consumer) => {
    try {
      const response = await api.consumer.getConsumer(consumer.id);
      setFormData({
        ...response.data,
        entryNumber: response.data.entryNumber.trim(),
        registrationNumber: response.data.registrationNumber.trim(),
        firstName: response.data.firstName.trim(),
        lastName: response.data.lastName.trim(),
        contactNumber: response.data.contactNumber.trim(),
        alternativeNumber: response.data.alternativeNumber?.trim() || "",
        email: response.data.email?.trim() || "",
        idProof: response.data.idProof?.trim() || "",
        buildingNumber: response.data.buildingNumber?.trim() || "",
        street: response.data.street?.trim() || "",
        city: response.data.city?.trim() || "",
        district: response.data.district?.trim() || "",
        state: response.data.state?.trim() || "",
        country: response.data.country?.trim() || "",
        openingBalance: response.data.openingBalance || 0,
      });
      setSelectedConsumerId(consumer.id);
      setIsEditing(true);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Error fetching consumer details:", error);
      toast({
        title: "Error",
        description: error.message ?? "Failed to load consumer details",
        variant: "destructive",
      });
    }
  };

  const handleDelete = (id: string) => {
    setSelectedConsumerId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedConsumerId) return;

    try {
      await api.consumer.deleteConsumer(selectedConsumerId);
      await fetchConsumers();

      toast({
        title: "Success",
        description: "Consumer deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting consumer:", error);
      toast({
        title: "Error",
        description: error.message ?? "Failed to delete consumer",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedConsumerId(null);
    }
  };

  const cancelForm = () => {
    setShowForm(false);
    setIsEditing(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      entryNumber: "",
      registrationNumber: "",
      firstName: "",
      lastName: "",
      categoryId: "",
      areaId: "",
      zoneId: "",
      divisionId: "",
      contactNumber: "",
      openingBalance: 0,
      balanceType: "receivable",
      status: "active",
      alternativeNumber: "",
      email: "",
      photo: "",
      idProof: "",
      buildingNumber: "",
      street: "",
      city: "",
      district: "",
      state: "",
      country: "",
    });
    setSelectedConsumerId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Trim all string fields before submission
      const data = {
        ...formData,
        entryNumber: formData.entryNumber.trim(),
        registrationNumber: formData.registrationNumber.trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        fullName: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        contactNumber: formData.contactNumber.trim(),
        alternativeNumber: formData.alternativeNumber?.trim(),
        email: formData.email?.trim(),
        idProof: formData.idProof?.trim(),
        buildingNumber: formData.buildingNumber?.trim(),
        street: formData.street?.trim(),
        city: formData.city?.trim(),
        district: formData.district?.trim(),
        state: formData.state?.trim(),
        country: formData.country?.trim(),
      };

      // Validate required fields after trimming
      if (
        !data.firstName ||
        !data.lastName ||
        !data.contactNumber ||
        !data.categoryId ||
        !data.areaId ||
        !data.zoneId ||
        !data.divisionId
        // !data.city ||
        // !data.state ||
        // !data.country
      ) {
        toast({
          title: "Error",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return;
      }

      if (isEditing && selectedConsumerId) {
        await api.consumer.updateConsumer(selectedConsumerId, data);
        toast({
          title: "Success",
          description: "Consumer updated successfully",
        });
      } else {
        await api.consumer.createConsumer(data);
        toast({
          title: "Success",
          description: "Consumer created successfully",
        });
      }

      await fetchConsumers();
      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error("Error saving consumer:", error);
      toast({
        title: "Error",
        description: error.message ? error.message : "Failed to save consumer",
        variant: "destructive",
      });
    }
  };

  // Handler for viewing consumer details
  const handleView = async (consumer: Consumer) => {
    try {
      setIsViewLoading(true);
      const response = await api.consumer.getConsumer(consumer.id);
      setViewConsumer(response.data);
      setShowViewModal(true);
    } catch (error) {
      console.error("Error fetching consumer details:", error);
      toast({
        title: "Error",
        description: error.message ?? "Failed to load consumer details",
        variant: "destructive",
      });
    } finally {
      setIsViewLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-0">
        <div className="flex flex-col w-full sm:w-auto">
          <h1 className="text-3xl font-bold tracking-tight">
            Consumer Management
          </h1>
          <p className="text-muted-foreground">Manage service consumers</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-2 w-full sm:w-auto">
          <Button 
            onClick={async () => {
              setIsEditing(false);
              resetForm();
              // Fetch entry number for new consumer
              try {
                const entryNumberResponse = await api.consumer.getEntryNumber();
                setFormData((prev) => ({
                  ...prev,
                  entryNumber: entryNumberResponse.data?.trim() || "",
                }));
              } catch (error) {
                console.error("Error fetching entry number:", error);
                toast({
                  title: "Error",
                  description: error.message ?? "Failed to get entry number",
                  variant: "destructive",
                });
              }
              setShowForm(!showForm);
            }}
            className="w-full sm:w-auto px-2 sm:px-4"
          >
            <PlusCircle className="mr-1 sm:mr-2 h-4 w-4" />
            Add Consumer
          </Button>
          {/* Upload Excel Button with confirmation dialog */}
          <input
            id="upload-excel-input"
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setSelectedExcelFile(file);
              setShowExcelConfirm(true);
              // Do not proceed with API call here; wait for user confirmation
              // Reset the input value so the same file can be uploaded again if needed
              e.target.value = "";
            }}
          />
          <Button
            variant="outline"
            onClick={() => {
              document.getElementById("upload-excel-input")?.click();
            }}
            disabled={isLoading}
            className="w-full sm:w-auto px-2 sm:px-4"
          >
            {isLoading ? (
              <Loader2 className="mr-1 sm:mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-1 sm:mr-2 h-4 w-4" />
            )}
            {isLoading ? "Importing..." : "Upload Excel"}
          </Button>
        </div>

        {/* Excel Import Confirmation Dialog */}
        <AlertDialog open={showExcelConfirm} onOpenChange={setShowExcelConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Excel Import</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to import this Excel file?
                <br />
                <span className="font-medium text-primary">
                  {selectedExcelFile?.name}
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setShowExcelConfirm(false);
                  setSelectedExcelFile(null);
                }}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  if (!selectedExcelFile) return;
                  const formData = new FormData();
                  formData.append("file", selectedExcelFile);
                  setShowExcelConfirm(false);
                  setIsLoading(true);
                  try {
                    await api.consumer.importExcel(formData);
                    toast({
                      title: "Success",
                      description: "Excel imported successfully",
                    });
                    await fetchConsumers();
                  } catch (error) {
                    console.error("Error importing Excel:", error);
                    toast({
                      title: "Error",
                      description: error.message ?? "Failed to import Excel",
                      variant: "destructive",
                    });
                  } finally {
                    setIsLoading(false);
                    setSelectedExcelFile(null);
                  }
                }}
                autoFocus
              >
                Proceed
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

      </div>

      {/* Responsive filter/search section */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end mb-4">
        {/* Search box takes more space on md+ */}
        <div className="md:col-span-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, entry, registration or contact number..."
              value={filters.query}
              onChange={(e) => handleFilterChange("query", e.target.value)}
              className="pl-8 w-full"
            />
          </div>
        </div>
        <div className="w-full">
          <Select
            value={filters.category}
            onValueChange={(value) => handleFilterChange("category", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {masterData.categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full">
          <Select
            value={filters.area}
            onValueChange={(value) => handleFilterChange("area", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All Areas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Areas</SelectItem>
              {masterData.areas.map((area) => (
                <SelectItem key={area.id} value={area.id}>
                  {area.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full">
          <Select
            value={filters.status}
            onValueChange={(value) => handleFilterChange("status", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {CONSUMER_STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Reset button right-aligned on md+, full-width on mobile */}
        <div className="w-full flex md:col-span-1 justify-end">
          <Button
            variant="outline"
            onClick={() => {
              setFilters({
                category: "all",
                area: "all",
                status: "all",
                query: "",
              });
              setCurrentPage(1);
            }}
            className="w-full md:w-auto"
          >
            Reset
          </Button>
        </div>
      </div>

      {showForm && (
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent size="xl" className="max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>
                {isEditing ? "Edit Consumer" : "Add New Consumer"}
              </DialogTitle>
              <DialogDescription>
                {isEditing
                  ? "Update consumer information"
                  : "Enter the details for the new consumer"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              {/* Photo Upload - moved to top */}
              <div className="mb-6">
                <div className="space-y-2">
                  <Label htmlFor="photo">Photo</Label>
                  <div className="flex items-center gap-4">
                    <div className="relative h-20 w-20 overflow-hidden rounded-full group bg-gray-100 flex items-center justify-center">
                      <img
                        src={
                          formData.photo
                            ? `${MEDIA_URL}${formData.photo}`
                            : "/placeholder.svg"
                        }
                        alt="Consumer preview"
                        className="h-full w-full object-cover"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          document
                            .getElementById("consumer-photo-input")
                            ?.click()
                        }
                      />
                      <button
                        type="button"
                        onClick={() =>
                          document
                            .getElementById("consumer-photo-input")
                            ?.click()
                        }
                        className="absolute bottom-1 right-1 bg-primary text-white rounded-full p-1 shadow-md border border-white flex items-center justify-center hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                        style={{ width: 32, height: 32 }}
                        tabIndex={0}
                        aria-label="Upload consumer photo"
                      >
                        <Upload className="h-4 w-4" />
                      </button>
                      <input
                        id="consumer-photo-input"
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleImageUpload(file);
                          }
                        }}
                        className="hidden"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="basic">Basic Details</TabsTrigger>
                  <TabsTrigger value="additional">Additional</TabsTrigger>
                </TabsList>
                <TabsContent value="basic">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Entry Number */}
                    <div className="space-y-2">
                      <Label htmlFor="entryNumber">Entry Number</Label>
                      <Input
                        id="entryNumber"
                        value={formData.entryNumber}
                        placeholder={isEditing ? "" : "Loading..."}
                        disabled={true}
                      />
                    </div>
                    {/* Registration Number */}
                    <div className="space-y-2">
                      <Label htmlFor="registrationNumber">
                        Registration Number{" "}
                        <span
                          className="text-red-500"
                          style={{ verticalAlign: "super", fontSize: "0.8em" }}
                        >
                          *
                        </span>
                      </Label>
                      <Input
                        id="registrationNumber"
                        value={formData.registrationNumber}
                        onChange={(e) =>
                          handleInputChange(
                            "registrationNumber",
                            e.target.value
                          )
                        }
                        placeholder="Registration number"
                        disabled={isEditing}
                      />
                    </div>
                    {/* First Name */}
                    <div className="space-y-2">
                      <Label htmlFor="firstName">
                        First Name{" "}
                        <span
                          className="text-red-500"
                          style={{ verticalAlign: "super", fontSize: "0.8em" }}
                        >
                          *
                        </span>
                      </Label>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <Input
                          id="firstName"
                          value={formData.firstName}
                          onChange={(e) =>
                            handleInputChange("firstName", e.target.value)
                          }
                          placeholder="Enter first name"
                          required
                        />
                      </div>
                    </div>
                    {/* Last Name */}
                    <div className="space-y-2">
                      <Label htmlFor="lastName">
                        Last Name{" "}
                        <span
                          className="text-red-500"
                          style={{ verticalAlign: "super", fontSize: "0.8em" }}
                        >
                          *
                        </span>
                      </Label>
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) =>
                          handleInputChange("lastName", e.target.value)
                        }
                        placeholder="Enter last name"
                        required
                      />
                    </div>
                    {/* Category */}
                    <div className="space-y-2">
                      <Label htmlFor="categoryId">
                        Category{" "}
                        <span
                          className="text-red-500"
                          style={{ verticalAlign: "super", fontSize: "0.8em" }}
                        >
                          *
                        </span>
                      </Label>
                      <Select
                        value={formData.categoryId}
                        onValueChange={(value) =>
                          handleInputChange("categoryId", value)
                        }
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {masterData.categories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Area */}
                    <div className="space-y-2">
                      <Label htmlFor="areaId">
                        Area{" "}
                        <span
                          className="text-red-500"
                          style={{ verticalAlign: "super", fontSize: "0.8em" }}
                        >
                          *
                        </span>
                      </Label>
                      <Select
                        value={formData.areaId}
                        onValueChange={(value) =>
                          handleInputChange("areaId", value)
                        }
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select area" />
                        </SelectTrigger>
                        <SelectContent>
                          {masterData.areas.map((area) => (
                            <SelectItem key={area.id} value={area.id}>
                              {area.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Zone */}
                    <div className="space-y-2">
                      <Label htmlFor="zoneId">
                        Zone{" "}
                        <span
                          className="text-red-500"
                          style={{ verticalAlign: "super", fontSize: "0.8em" }}
                        >
                          *
                        </span>
                      </Label>
                      <Select
                        value={formData.zoneId}
                        onValueChange={(value) =>
                          handleInputChange("zoneId", value)
                        }
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select zone" />
                        </SelectTrigger>
                        <SelectContent>
                          {masterData.zones.map((zone) => (
                            <SelectItem key={zone.id} value={zone.id}>
                              {zone.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Division */}
                    <div className="space-y-2">
                      <Label htmlFor="divisionId">
                        Division{" "}
                        <span
                          className="text-red-500"
                          style={{ verticalAlign: "super", fontSize: "0.8em" }}
                        >
                          *
                        </span>
                      </Label>
                      <Select
                        value={formData.divisionId}
                        onValueChange={(value) =>
                          handleInputChange("divisionId", value)
                        }
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select division" />
                        </SelectTrigger>
                        <SelectContent>
                          {masterData.divisions.map((division) => (
                            <SelectItem key={division.id} value={division.id}>
                              {division.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Contact Number */}
                    <div className="space-y-2">
                      <Label htmlFor="contactNumber">
                        Contact Number{" "}
                        <span
                          className="text-red-500"
                          style={{ verticalAlign: "super", fontSize: "0.8em" }}
                        >
                          *
                        </span>
                      </Label>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <Input
                          id="contactNumber"
                          type="tel"
                          pattern="[0-9]*"
                          maxLength={10}
                          value={formData.contactNumber}
                          onKeyDown={handleNumericInput}
                          onChange={(e) =>
                            handleInputChange("contactNumber", e.target.value)
                          }
                          placeholder="Enter contact number"
                          required
                        />
                      </div>
                    </div>
                    {/* Opening Balance and Balance Type */}
                    <div className="space-y-2">
                      <Label htmlFor="openingBalance">Opening Balance</Label>
                      <div className="flex gap-2">
                        <Input
                          id="openingBalance"
                          type="number"
                          value={formData.openingBalance.toString()}
                          onChange={(e) => {
                            // Remove non-digit characters and limit to 7 digits
                            const value = e.target.value
                              .replace(/[^0-9]/g, "")
                              .slice(0, 7);
                            // Convert to number, fallback to 0 if empty
                            handleInputChange(
                              "openingBalance",
                              value ? parseInt(value, 10) : 0
                            );
                          }}
                          maxLength={7}
                          max={9999999}
                          placeholder="Enter opening balance"
                          className="flex-1"
                        />
                        <Select
                          value={formData.balanceType}
                          onValueChange={(value) =>
                            handleInputChange("balanceType", value)
                          }
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue placeholder="Balance Type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="receivable">
                              Receivable
                            </SelectItem>
                            <SelectItem value="payable">Payable</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) =>
                          handleInputChange("status", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {CONSUMER_STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="additional">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Alternative Number */}
                    <div className="space-y-2">
                      <Label htmlFor="alternativeNumber">
                        Alternative Number
                      </Label>
                      <Input
                        id="alternativeNumber"
                        type="tel"
                        pattern="[0-9]*"
                        maxLength={10}
                        onKeyDown={handleNumericInput}
                        value={formData.alternativeNumber}
                        onChange={(e) =>
                          handleInputChange("alternativeNumber", e.target.value)
                        }
                        placeholder="Enter alternative number"
                      />
                    </div>
                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) =>
                            handleInputChange("email", e.target.value)
                          }
                          placeholder="Enter email address"
                        />
                      </div>
                    </div>
                    {/* ID Proof */}
                    <div className="space-y-2">
                      <Label htmlFor="idProof">ID Proof</Label>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <Input
                          id="idProof"
                          value={formData.idProof}
                          onChange={(e) =>
                            handleInputChange("idProof", e.target.value)
                          }
                          placeholder="Enter ID proof type"
                        />
                      </div>
                    </div>
                    {/* Location Information */}
                    <div className="space-y-2">
                      <Label htmlFor="buildingNumber">Building No.</Label>
                      <div className="flex items-center gap-2">
                        <Home className="h-4 w-4 text-muted-foreground" />
                        <Input
                          id="buildingNumber"
                          value={formData.buildingNumber}
                          onChange={(e) =>
                            handleInputChange("buildingNumber", e.target.value)
                          }
                          placeholder="Enter building number"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="street">Street Name</Label>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <Input
                          id="street"
                          value={formData.street}
                          onChange={(e) =>
                            handleInputChange("street", e.target.value)
                          }
                          placeholder="Enter street name"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) =>
                          handleInputChange("city", e.target.value)
                        }
                        placeholder="Enter city"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="district">District</Label>
                      <Input
                        id="district"
                        value={formData.district}
                        onChange={(e) =>
                          handleInputChange("district", e.target.value)
                        }
                        placeholder="Enter district"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) =>
                          handleInputChange("state", e.target.value)
                        }
                        placeholder="Enter state"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        value={formData.country}
                        onChange={(e) =>
                          handleInputChange("country", e.target.value)
                        }
                        placeholder="Enter country"
                        required
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
              <DialogFooter>
                <Button variant="outline" type="button" onClick={cancelForm}>
                  Cancel
                </Button>
                <Button type="submit">
                  {isEditing ? "Update Consumer" : "Add Consumer"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="h-10">
                <TableHead className="whitespace-nowrap px-3 py-2">#</TableHead>
                <TableHead className="whitespace-nowrap px-3 py-2">Name</TableHead>
                <TableHead className="whitespace-nowrap px-3 py-2">Reg. Number</TableHead>
                <TableHead className="whitespace-nowrap px-3 py-2">Contact</TableHead>
                <TableHead className="whitespace-nowrap px-3 py-2">Category</TableHead>
                <TableHead className="whitespace-nowrap px-3 py-2">Area</TableHead>
                <TableHead className="whitespace-nowrap px-3 py-2">Status</TableHead>
                <TableHead className="w-[50px] px-3 py-2">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {consumers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center py-2 text-muted-foreground"
                  >
                    {filters.query ||
                    filters.category ||
                    filters.area ||
                    filters.status
                      ? "No consumers match the selected filters"
                      : "No consumers found"}
                  </TableCell>
                </TableRow>
              ) : (
                consumers.map((consumer) => (
                  <TableRow key={consumer.id} className="h-10">
                    <TableCell className="font-medium">
                      {consumer.entryNumber}
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <div className="hidden sm:flex w-8 h-8 rounded-full bg-gray-100 items-center justify-center overflow-hidden border">
                          <img
                            src={
                              consumer.photo
                                ? `${MEDIA_URL}${consumer.photo}`
                                : "/placeholder.svg"
                            }
                            alt={consumer.fullName}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {capitalizeFirstLetter(consumer.fullName)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {consumer.registrationNumber?.trim()
                        ? consumer.registrationNumber
                        : "—"}
                    </TableCell>
                    <TableCell>{consumer.contactNumber}</TableCell>
                    <TableCell>{consumer.category?.name}</TableCell>
                    <TableCell>{consumer.area?.name}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-block px-2 py-1 text-xs rounded-full ${
                          consumer.status?.toLowerCase() === "active"
                            ? "bg-green-100 text-green-800"
                            : consumer.status?.toLowerCase() === "inactive"
                            ? "bg-gray-100 text-gray-800"
                            : consumer.status?.toLowerCase() === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {consumer.status?.charAt(0).toUpperCase() +
                          consumer.status?.slice(1).toLowerCase()}
                      </span>
                    </TableCell>
                    <TableCell className="px-3 py-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleView(consumer)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(consumer)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(consumer.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex flex-col items-center sm:flex-row sm:justify-between mt-4 gap-2">
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                className="w-full sm:w-auto"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the
              consumer and all associated data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Consumer Modal */}
      {showViewModal && viewConsumer && (
        <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Consumer Details</DialogTitle>
              <DialogDescription>
                View detailed information about the consumer
              </DialogDescription>
            </DialogHeader>
            {isViewLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-4">
                  <div className="flex justify-center items-center">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                    <span>Loading...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label>Photo</Label>
                    <div className="flex items-center gap-4">
                      <div className="relative h-20 w-20 overflow-hidden rounded-full bg-gray-100 flex items-center justify-center">
                        {viewConsumer.photo ? (
                          <img
                            src={`${MEDIA_URL}${viewConsumer.photo}`}
                            alt="Consumer"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="text-muted-foreground">
                            No Photo
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Entry Number</Label>
                    <p className="text-sm">{viewConsumer.entryNumber}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Registration Number</Label>
                    <p className="text-sm">
                      {viewConsumer.registrationNumber || "—"}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <p className="text-sm">{viewConsumer.fullName}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>First Name</Label>
                    <p className="text-sm">{viewConsumer.firstName}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name</Label>
                    <p className="text-sm">{viewConsumer.lastName}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <p className="text-sm">
                      {viewConsumer.category?.name || "—"}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Area</Label>
                    <p className="text-sm">{viewConsumer.area?.name || "—"}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Zone</Label>
                    <p className="text-sm">{viewConsumer.zone?.name || "—"}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Division</Label>
                    <p className="text-sm">
                      {viewConsumer.division?.name || "—"}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Number</Label>
                    <p className="text-sm">{viewConsumer.contactNumber}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Alternative Number</Label>
                    <p className="text-sm">
                      {viewConsumer.alternativeNumber || "—"}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <p className="text-sm">{viewConsumer.email || "—"}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>ID Proof</Label>
                    <p className="text-sm">{viewConsumer.idProof || "—"}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Opening Balance</Label>
                    <p className="text-sm">{viewConsumer.openingBalance}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Balance Type</Label>
                    <p className="text-sm">{viewConsumer.balanceType}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <p className="text-sm">{viewConsumer.status}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Building Number</Label>
                    <p className="text-sm">
                      {viewConsumer.buildingNumber || "—"}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Street</Label>
                    <p className="text-sm">{viewConsumer.street || "—"}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <p className="text-sm">{viewConsumer.city || "—"}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>District</Label>
                    <p className="text-sm">{viewConsumer.district || "—"}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <p className="text-sm">{viewConsumer.state || "—"}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <p className="text-sm">{viewConsumer.country || "—"}</p>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewModal(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ConsumersPage;
